// Market-cap treemap for the 3D heatmap (ported from radar's heatmap-layout).
// Tile footprint = share of mapped market cap; colour = 24h price change.

import type { BoardToken } from "../api";
import { tokenName } from "../format";

/** One mapped tile: a token, or the aggregated "other" remainder. */
export interface HeatTile {
  id: string;
  symbol: string;
  name: string;
  mcap: number;
  price: number | null;
  /** 24h price change as a fraction (0.012 = +1.2%); null until history exists. */
  change: number | null;
  chains: string[];
  /** Number of tokens folded into this tile (1 for a single token). */
  count: number;
  /** The issuing venue, on the market-wide map; absent on a venue's own map. */
  venue?: string;
}

/** A board token, optionally tagged with its venue (the all-venues map). */
export type HeatSource = BoardToken & { venue?: string };

export interface CapBlock<T extends { id: string; mcap: number } = HeatTile> {
  token: T;
  x: number;
  z: number;
  width: number;
  depth: number;
}

export interface HeatmapData {
  tiles: HeatTile[];
  /** Tokens left off the map: no market cap, or one that is not positive. */
  excluded: number;
  /** Tokens folded into the "other" tile. */
  folded: number;
}

export const OTHER_ID = "__other__";
export const DEFAULT_TILE_LIMIT = 150;

const usable = (cap: number | null): cap is number => cap !== null && Number.isFinite(cap) && cap > 0;

/**
 * Picks the tokens to map: the largest `limit` by cap, plus one "other" tile
 * holding the rest so the footprints still add up to the whole venue.
 */
export function prepareTiles(tokens: readonly HeatSource[], limit = DEFAULT_TILE_LIMIT): HeatmapData {
  const mapped = tokens
    .filter((t) => usable(t.market_cap))
    .map<HeatTile>((t) => ({
      id: t.id,
      symbol: t.symbol,
      name: tokenName(t.name),
      mcap: t.market_cap!,
      price: t.price,
      change: t.change_24h.price !== null && Number.isFinite(t.change_24h.price) ? t.change_24h.price : null,
      chains: t.chains ?? [t.chain],
      count: 1,
      ...(t.venue ? { venue: t.venue } : {}),
    }))
    .sort((a, b) => b.mcap - a.mcap || a.id.localeCompare(b.id));
  const excluded = tokens.length - mapped.length;
  if (mapped.length <= limit) return { tiles: mapped, excluded, folded: 0 };

  const kept = mapped.slice(0, Math.max(0, limit));
  const rest = mapped.slice(Math.max(0, limit));
  const mcap = rest.reduce((sum, t) => sum + t.mcap, 0);
  const withChange = rest.filter((t) => t.change !== null);
  const changeCap = withChange.reduce((sum, t) => sum + t.mcap, 0);
  const change = withChange.length && changeCap > 0 ? withChange.reduce((sum, t) => sum + t.change! * t.mcap, 0) / changeCap : null;
  const chains = [...new Set(rest.flatMap((t) => t.chains))];
  const other: HeatTile = {
    id: OTHER_ID,
    symbol: "OTHER",
    name: `${rest.length} smaller tokens`,
    mcap,
    price: null,
    change,
    chains,
    count: rest.length,
  };
  return { tiles: [...kept, other], excluded, folded: rest.length };
}

// Balanced binary treemap: every footprint preserves its share of total cap.
export function layoutMarketCaps<T extends { id: string; mcap: number }>(rows: T[]): CapBlock<T>[] {
  const tokens = rows.filter((row) => usable(row.mcap)).sort((a, b) => b.mcap - a.mcap || a.id.localeCompare(b.id));
  const blocks: CapBlock<T>[] = [];
  function split(items: T[], x: number, z: number, width: number, depth: number): void {
    const first = items[0];
    if (!first) return;
    if (items.length === 1) {
      blocks.push({ token: first, x, z, width, depth });
      return;
    }
    const total = items.reduce((sum, row) => sum + row.mcap, 0);
    let left = first.mcap;
    let cut = 1;
    while (cut < items.length - 1 && Math.abs(left + items[cut]!.mcap - total / 2) < Math.abs(left - total / 2)) {
      left += items[cut++]!.mcap;
    }
    const ratio = left / total;
    if (width >= depth) {
      split(items.slice(0, cut), x, z, width * ratio, depth);
      split(items.slice(cut), x + width * ratio, z, width * (1 - ratio), depth);
    } else {
      split(items.slice(0, cut), x, z, width, depth * ratio);
      split(items.slice(cut), x, z + depth * ratio, width, depth * (1 - ratio));
    }
  }
  split(tokens, -500, -350, 1000, 700);
  return blocks;
}
